import {
    CircularProgress,
    createStyles,
    Grid,
    Theme,
    withStyles,
    WithStyles,
} from "@material-ui/core";
import classNames from "classnames";
import { parse } from "query-string";
import React from "react";
import { getGlobalUserId } from "shared/src/framework/network/headers";
import { Category } from "shared/src/models/category";
import { Id } from "shared/src/models/framework/db";
import { UserInfo } from "shared/src/models/user/user-info";
import { categoryGet } from "shared/src/network/requests/category/category-req";
import { addComment, getPolls } from "shared/src/network/requests/poll/poll-req";
import { PollCard, RequestedRecord } from "shared/src/network/requests/poll/poll-req-models";
import { userGet } from "shared/src/network/requests/user/user-req";
import { Analytic, getOpenDate, getPollListType } from "../analytic/analytic";
import PollSwiper from "../components/PollSwiper";
import { SocketContext } from "../context/socket";
import { commonClasses, spacing } from "../styles";

const resultBarHeight = 116;
const styles = (theme: Theme) =>
    createStyles({
        ...commonClasses,
        ...spacing(theme),
        loader: {
            "& circle": {
                animation: "none",
            },
        },
        resultBarContainer: {
            height: `${resultBarHeight}px`,
            width: "100%",
            borderBottom: `4px solid ${theme.palette.grey[300]}`,
        },
    });

type Props = WithStyles<typeof styles> & {
    className?: string;
    setShowUserGuide?: (showUserGuide: boolean) => void;
};

type State = {
    isLoadingView: boolean;
    polls: Array<Id<PollCard>>;
    resultPoll?: Id<PollCard>;
};

class Component extends React.Component<Props, State> {
    currentUser?: string;
    requestingPolls: { top: boolean; bottom: boolean };
    static contextType = SocketContext;
    context!: React.ContextType<typeof SocketContext>;
    user: UserInfo = {};
    categories?: Id<Category>[];
    pollListType?: string[];
    availableHeight = window.innerHeight - 88;
    constructor(props: Props) {
        super(props);
        this.requestingPolls = { top: false, bottom: false };
        this.state = {
            isLoadingView: true,
            polls: [],
        };
        this.currentUser = getGlobalUserId();
        this.pollListType = getPollListType();
        userGet({}).then((response) => (this.user = response.user || {}));
    }

    componentDidMount = async () => {
        window.addEventListener("resize", this.handleWindowResize);
        this.categories = await categoryGet({});
        this.initPolls(this.getCategoryIdsFromQueryParams()).then(() => {
            const openDate = getOpenDate();
            Analytic.addEvent({
                event: "load_time",
                eventValue: openDate + " - " + (new Date().valueOf() - openDate.valueOf()),
                defaultGTMConfig: "false",
            });
        });
    };

    componentWillUnmount = () => {
        window.removeEventListener("resize", this.handleWindowResize);
    };

    handleWindowResize = () => {
        if (window.innerHeight > this.availableHeight) {
            this.availableHeight = window.innerHeight - 88;
            this.forceUpdate();
        }
    };

    initPolls = async (categoryIds?: string[]) => {
        try {
            if (this.currentUser === null) return;
            if (this.user.categoryIds?.length) categoryIds = this.user.categoryIds;
            const requestedPollId = this.getPollIdFromQueryParams();
            const apiResponse = await getPolls({
                requestedRecordType: RequestedRecord.NEXT,
                categoryIds,
                requestedPollId,
                pollListType: this.pollListType,
            });
            this.setState({ polls: apiResponse?.pollList || [] }, () => {
                this.setState({ isLoadingView: false });
            });
        } catch {}
    };

    getPollIdFromQueryParams = () => {
        const queryStringParams = parse(window.location.search);
        return typeof queryStringParams.id === "string" ? queryStringParams.id : undefined;
    };

    getCategoryIdsFromQueryParams = () => {
        const queryStringParams = parse(window.location.search);
        let categoryIds: Array<string> | undefined = undefined;
        let categoryById =
            queryStringParams.categoryIds !== null ? queryStringParams.categoryIds : undefined;

        if (typeof categoryById === "string") categoryIds = categoryById.split(",");

        let categorySlug =
            queryStringParams.catSlug !== null ? queryStringParams.catSlug : undefined;
        if (typeof categorySlug === "string") {
            categorySlug = this.categories
                ?.filter((e) => categorySlug?.includes(e.slug || ""))
                .map((x) => x.id);
            categoryIds = (categoryIds || []).concat(categorySlug || []);
        }
        return categoryIds;
    };

    handleLoadMorePolls = async (currentIndex: number) => {
        const { polls } = this.state;
        let categoryIds = this.getCategoryIdsFromQueryParams();
        // If there is no categories in URL then use user's choice
        if (!Boolean(categoryIds?.length)) {
            categoryIds = this.user?.categoryIds;
        }
        if (this.currentUser === null) return;
        if (currentIndex > polls.length - 6) {
            if (this.requestingPolls.bottom) return;
            try {
                this.requestingPolls.bottom = true;
                const lastPollId = polls.length > 0 ? polls[polls.length - 1].id : "";
                const apiResponse = await getPolls({
                    lastPollId: lastPollId,
                    requestedRecordType: RequestedRecord.NEXT,
                    // userId: this.currentUser,
                    categoryIds,
                    pollListType: this.pollListType,
                });
                if (apiResponse) {
                    this.setState((state) => {
                        const pollIdList = state.polls.map((each) => each.id);
                        const dedupedList = apiResponse.pollList.filter(
                            (each) => !pollIdList.includes(each.id)
                        );
                        return {
                            polls: [...state.polls, ...dedupedList],
                        };
                    });
                }
            } finally {
                this.requestingPolls.bottom = false;
            }
        } else if (currentIndex < 5) {
            try {
                if (this.requestingPolls.top) return;
                const firstPollId = polls.length > 0 ? polls[0].id : "";
                this.requestingPolls.top = true;
                const apiResponse = await getPolls({
                    lastPollId: firstPollId,
                    requestedRecordType: RequestedRecord.PREVIOUS,
                    // userId: this.currentUser,
                    categoryIds,
                    pollListType: this.pollListType,
                });
                if (apiResponse) {
                    this.setState((state) => {
                        const pollIdList = state.polls.map((each) => each.id);
                        const dedupedList = apiResponse.pollList.filter(
                            (each) => !pollIdList.includes(each.id)
                        );
                        return {
                            polls: [...dedupedList, ...state.polls],
                        };
                    });
                }
            } finally {
                this.requestingPolls.top = false;
            }
        }
    };

    handleCommentSend = (pollId: string, comment: string): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            const { polls } = this.state;
            const poll = polls.find((each) => each.id === pollId);
            if (!poll) return reject(`No poll with poll id ${pollId}`);
            if (!this.currentUser) return reject("Current user is null");
            resolve();
            await addComment({
                pollId: poll.id,
                userId: this.currentUser,
                message: comment,
            });
            poll.totalComment += 1;
            this.updatePollInList(poll);
        });
    };

    handleOptionSelected = (poll: Id<PollCard>) => {
        poll.totalVote += 1;
        this.updatePollInList(poll);
    };

    // This function will use object destructuring to replace poll object,
    // So that component detects property change for the poll
    updatePollInList = (poll: Id<PollCard>) => {
        this.setState((state) => {
            const polls = state.polls.map((each) => {
                if (each.id === poll.id) {
                    return { ...poll };
                }
                return each;
            });
            return { polls: [...polls] };
        });
    };

    handleInterestChange = (selectedCategoriesId: string[]) => {
        if (this.user) this.user.categoryIds = selectedCategoriesId;
        this.initPolls();
    };

    render = () => {
        const { classes, className, setShowUserGuide } = this.props;
        const { isLoadingView, polls } = this.state;
        const defaultActivePollId = this.getPollIdFromQueryParams();
        const height = this.availableHeight || 0;
        if (isLoadingView) {
            return (
                <Grid
                    container
                    alignItems="center"
                    justifyContent="center"
                    className={classNames(classes.fullHeight)}>
                    <CircularProgress className={classes.loader} disableShrink />
                </Grid>
            );
        }
        return (
            <Grid
                container
                direction="column"
                alignItems="center"
                justifyContent="center"
                wrap="nowrap"
                className={classNames(classes.fullHeight, className)}>
                <Grid item className={classes.fullWidth} sx={{ height, overflow: "hidden" }}>
                    <PollSwiper
                        polls={polls}
                        uid={this.currentUser}
                        availableHeight={height}
                        onLoadMorePolls={this.handleLoadMorePolls}
                        onCommentSend={this.handleCommentSend}
                        onOptionSelected={this.handleOptionSelected}
                        user={this.user}
                        categories={this.categories}
                        defaultActivePollId={defaultActivePollId}
                        onInterestChange={this.handleInterestChange}
                        setShowUserGuide={setShowUserGuide}
                    />
                </Grid>
            </Grid>
        );
    };
}

const Polls = withStyles(styles)(Component);

export default Polls;
